"use client"

import { Check, X, AlertCircle, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useUploads, type UploadItem } from "@/lib/upload/upload-store"

/**
 * Toaster flutuante global de uploads. Aparece (canto inferior) sempre que há
 * upload em andamento, mostrando progresso e um botão de cancelar por upload.
 * Resolve o caso do usuário achar que "bugou" num arquivo grande e o de
 * cancelamentos involuntários. Montado uma única vez no layout raiz.
 */
export function UploadProgress() {
  const uploads = useUploads()
  if (uploads.length === 0) return null

  return (
    <div
      className={cn(
        "fixed z-[90] flex flex-col gap-2",
        // Mobile: faixa inferior acima da bottom-nav, respeitando safe-area.
        "left-3 right-3 bottom-[calc(5rem+var(--wt-safe-bottom))]",
        // Desktop: canto inferior direito, largura fixa.
        "md:left-auto md:right-4 md:bottom-4 md:w-[360px]",
      )}
      role="status"
      aria-live="polite"
    >
      {uploads.map((u) => (
        <UploadCard key={u.id} item={u} />
      ))}
    </div>
  )
}

function UploadCard({ item }: { item: UploadItem }) {
  const { label, progress, status, error, indeterminate } = item
  const enviando = status === "uploading"

  return (
    <div className="rounded-xl border border-border bg-surface-2 shadow-glow-lg p-3 animate-in fade-in slide-in-from-bottom-2">
      <div className="flex items-center gap-2.5">
        <div className="shrink-0">
          {status === "success" ? (
            <span className="flex size-7 items-center justify-center rounded-full bg-success/15 text-success">
              <Check className="size-4" />
            </span>
          ) : status === "error" ? (
            <span className="flex size-7 items-center justify-center rounded-full bg-destructive/15 text-destructive">
              <AlertCircle className="size-4" />
            </span>
          ) : (
            <span className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Loader2 className="size-4 animate-spin" />
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{label}</p>
          <p
            className={cn(
              "text-xs",
              status === "error" ? "text-destructive" : "text-fg-muted",
            )}
          >
            {status === "success"
              ? "Enviado"
              : status === "error"
                ? error || "Falha no envio"
                : indeterminate
                  ? "Enviando…"
                  : progress >= 100
                    ? "Processando…"
                    : `Enviando… ${progress}%`}
          </p>
        </div>

        {enviando && (
          <button
            type="button"
            onClick={item.cancel}
            aria-label="Cancelar envio"
            className="shrink-0 flex size-7 items-center justify-center rounded-full text-fg-muted hover:bg-surface-3 hover:text-foreground transition-colors active:scale-90"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {enviando && (
        <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
          {indeterminate ? (
            <div className="h-full w-1/3 gradient-brand rounded-full animate-upload-indeterminate" />
          ) : (
            <div
              className="h-full gradient-brand rounded-full transition-[width] duration-200"
              style={{ width: `${progress}%` }}
            />
          )}
        </div>
      )}
    </div>
  )
}
